import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { whole } from "./frame";

/**
 * A panel over the whole screen, drawn OUTSIDE the post card.
 *
 * It has to be a portal: a card lifts a little while the mouse is over it
 * (`group-hover:-translate-y`), and a lifted card becomes the frame that
 * "cover the screen" is measured against — so an overlay drawn inside the card
 * was trapped in it and jumped as the mouse moved (seen 2026-09-15).
 *
 * Also: the wall behind cannot scroll while this is open, the panel itself
 * scrolls if the screen is short, Esc closes, and the keyboard stays inside.
 */
export function Overlay({ label, children, onClose }) {
  const [shown, setShown] = useState(false);
  const panel = useRef(null);
  const closer = useRef(null);

  const close = useCallback(() => {
    setShown(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (closer.current) closer.current.focus();
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); }
      if (e.key === "Tab" && panel.current) {
        const buttons = Array.from(panel.current.querySelectorAll("button:not([disabled])"));
        if (!buttons.length) return;
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      className={"fixed inset-0 z-[70] grid place-items-center overflow-y-auto p-[16px] " +
                 "bg-[rgba(10,16,28,0.55)] backdrop-blur-[3px] " +
                 "transition-opacity duration-200 ease-out motion-reduce:transition-none " +
                 (shown ? "opacity-100" : "opacity-0")}
    >
      <div
        ref={panel}
        className={"w-full max-w-[880px] max-h-[92vh] overflow-y-auto rounded-[16px] bg-white dark:bg-neutral-900 " +
                   "p-[16px] shadow-[0_30px_60px_-22px_rgba(0,0,0,0.5)] " +
                   "transition-transform duration-300 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none " +
                   (shown ? "translate-y-0 scale-100" : "translate-y-[14px] scale-[0.98]")}
      >
        {children({ close, closerRef: closer })}
      </div>
    </div>,
    document.body
  );
}

/**
 * One photo at a time, as large as the screen allows: the "see it bigger" every
 * style uses. Sam, 2026-09-15: the posts must be clear enough for grandparents.
 */
export function PhotoViewer({ photos, lines = [], title, counter = "Photo", startIndex = 0, onClose, pop = false }) {
  const [index, setIndex] = useState(startIndex);
  const [loaded, setLoaded] = useState(false);
  // A Pop-Up Book page stands itself up as it arrives, the way a paper one does.
  const [standing, setStanding] = useState(!pop);
  const startX = useRef(null);
  const at = Math.max(0, Math.min(photos.length - 1, index));
  const turn = useCallback(
    (step) => setIndex((i) => Math.max(0, Math.min(photos.length - 1, i + step))),
    [photos.length]
  );

  useEffect(() => {
    setLoaded(false);
    if (!pop) return undefined;
    setStanding(false);
    const frame = requestAnimationFrame(() => setStanding(true));
    return () => cancelAnimationFrame(frame);
  }, [index, pop]);

  // Fetch the pages either side quietly, so turning a page is instant.
  useEffect(() => {
    [at - 1, at + 1].forEach((i) => {
      if (i >= 0 && i < photos.length) {
        const ahead = new Image();
        ahead.src = whole(photos[i].url, 1800);
      }
    });
  }, [at, photos]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") turn(1);
      if (e.key === "ArrowLeft") turn(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [turn]);

  const arrow =
    "grid place-items-center h-[44px] w-[44px] rounded-full border border-dupontGoldLt/60 bg-white dark:bg-neutral-800 " +
    "text-[22px] leading-none text-dupontBlue dark:text-darkText shadow-sm disabled:opacity-35 " +
    "hover:bg-dupontCream dark:hover:bg-neutral-700 transition-colors motion-reduce:transition-none";

  return (
    <Overlay label={title} onClose={onClose}>
      {({ close, closerRef }) => (
        <>
          <div className="flex items-baseline gap-[10px] mb-[12px]">
            <h4 className="m-0 min-w-0 break-words [overflow-wrap:anywhere] font-display font-semibold text-[18px] text-dupontBlue dark:text-darkText">{title}</h4>
            <span className="ml-auto font-blairBody text-[12px] text-gray-500 tabular-nums" aria-live="polite">
              {counter} {at + 1} of {photos.length}
            </span>
            <button ref={closerRef} type="button" onClick={close} aria-label="Close"
                    className="pl-[8px] text-[24px] leading-none text-gray-400 hover:text-dupontBlue">×</button>
          </div>

          <div
            className="rounded-[10px] bg-dupontCream dark:bg-neutral-800 p-[10px]"
            onPointerDown={(e) => { startX.current = e.clientX; }}
            onPointerUp={(e) => {
              if (startX.current === null) return;
              const dx = e.clientX - startX.current;
              if (Math.abs(dx) > 40) turn(dx < 0 ? 1 : -1);
              startX.current = null;
            }}
          >
            {/* A small copy of the SAME shape stands in while the large one arrives,
                so the picture only sharpens — it never appears to zoom (it used to
                be a square crop). The <img> is not re-keyed, so the browser keeps
                the page you are leaving on screen until the next one is ready. */}
            <div className="relative mx-auto w-fit [perspective:1200px]">
              <div data-page-pop
                   className={"origin-bottom transition-transform duration-500 ease-[cubic-bezier(.2,.9,.25,1)] " +
                              "motion-reduce:transition-none motion-reduce:[transform:none] " +
                              (standing ? "[transform:rotateX(0deg)]" : "[transform:rotateX(-72deg)_translateY(10px)]")}>
              <img aria-hidden="true" src={whole(photos[at].url, 640)} alt=""
                   className={"block max-h-[68vh] w-auto max-w-full rounded-[6px] object-contain blur-[2px] " +
                              "transition-opacity duration-200 ease-out motion-reduce:transition-none " +
                              (loaded ? "opacity-0" : "opacity-100")} />
              <img src={whole(photos[at].url, 1800)} alt={lines[at] || `${counter} ${at + 1}`}
                   onLoad={() => setLoaded(true)}
                   className={"absolute inset-0 m-auto block max-h-[68vh] w-auto max-w-full rounded-[6px] object-contain " +
                              "transition-opacity duration-200 ease-out motion-reduce:transition-none " +
                              (loaded ? "opacity-100" : "opacity-0")} />
              </div>
            </div>
            {lines[at] ? (
              <p className="mt-[10px] mb-0 mx-[2px] font-marginNote font-semibold text-[18px] text-dupontBlue dark:text-darkText">
                {lines[at]}
              </p>
            ) : null}
          </div>

          <div className="mt-[12px] flex items-center justify-between gap-[10px]">
            <button type="button" onClick={() => turn(-1)} disabled={at === 0} aria-label="Previous photo" className={arrow}>‹</button>
            <div className="flex gap-[7px]" aria-hidden="true">
              {photos.map((_, i) => (
                <span key={i} className={"h-[7px] w-[7px] rounded-full transition-[background-color,transform] duration-200 " +
                                         (i === at ? "bg-dupontGold scale-125" : "bg-dupontGoldLt/40")} />
              ))}
            </div>
            <button type="button" onClick={() => turn(1)} disabled={at === photos.length - 1} aria-label="Next photo" className={arrow}>›</button>
          </div>
        </>
      )}
    </Overlay>
  );
}

export default Overlay;
